import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  OnInit,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScheduleGridCellDirective } from './schedule-grid-cell.directive';
import {
  ScheduleBlock,
  ScheduleRow,
  ScheduleGroup,
  ScheduleViewMode,
  ScheduleInfoColumn,
  ScheduleCellClickEvent,
  ScheduleBlockClickEvent,
  SchedulePeriodChangeEvent,
  ScheduleRangeSelectEvent,
} from './schedule-grid.models';

/** Internal helper: one column (day) in the grid */
interface DayColumn {
  date: Date;
  isoDate: string;       // 'YYYY-MM-DD'
  dayOfMonth: number;
  dayOfWeek: number;      // 0=Sun … 6=Sat
  dayName: string;        // 'Man', 'Tir', …
  isWeekend: boolean;
  isToday: boolean;
  weekNumber: number;
  monthIndex: number;     // 0-based
  monthLabel: string;     // 'Februar'
}

/** Internal helper for week header spans */
interface WeekSpan {
  weekNumber: number;
  colSpan: number;
  year: number;
}

/** Internal helper for month header spans */
interface MonthSpan {
  label: string;
  colSpan: number;
}

/** Palette of shift colors */
const BLOCK_PALETTE = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#14b8a6', // teal
];

@Component({
  selector: 'konti-schedule-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ScheduleGridCellDirective],
  templateUrl: './schedule-grid.component.html',
  styleUrls: ['./schedule-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleGridComponent implements AfterContentInit, OnChanges, OnInit, OnDestroy {

  // ── Inputs ──────────────────────────────────────────────

  /** Radene som skal vises (ansatte, ressurser, etc.) */
  @Input() rows: ScheduleRow[] = [];

  /** Valgfrie grupper for å gruppere rader */
  @Input() groups: ScheduleGroup[] = [];

  /** Visningsmodus */
  @Input() viewMode: ScheduleViewMode = 'week';

  /** Startdato for visningen (default: mandag denne uken) */
  @Input() startDate?: Date;

  /** Vis ukenummer-header */
  @Input() showWeekNumbers = true;

  /** Vis månedsheader (relevant for two-weeks og month) */
  @Input() showMonthHeader = true;

  /** Ekstra info-kolonner til venstre */
  @Input() infoColumns: ScheduleInfoColumn[] = [];

  /** Bredde på navnkolonnen (px) */
  @Input() labelWidth = 200;

  /** Vis loader */
  @Input() showLoader = false;

  /** Om "i dag"-kolonne skal markeres */
  @Input() highlightToday = true;

  /** Tillat kollaps/ekspandering av grupper */
  @Input() collapsibleGroups = true;

  /** Tillat dra-markering over flere dager */
  @Input() enableRangeSelect = false;

  // ── Outputs ─────────────────────────────────────────────

  /** Celle klikket */
  @Output() cellClick = new EventEmitter<ScheduleCellClickEvent>();

  /** Blokk klikket */
  @Output() blockClick = new EventEmitter<ScheduleBlockClickEvent>();

  /** Periode endret (navigering) */
  @Output() periodChange = new EventEmitter<SchedulePeriodChangeEvent>();

  /** ViewMode endret */
  @Output() viewModeChange = new EventEmitter<ScheduleViewMode>();

  /** Bruker har markert et datoomrade i en rad (drag-select) */
  @Output() rangeSelect = new EventEmitter<ScheduleRangeSelectEvent>();

  // ── Content children ────────────────────────────────────

  @ContentChildren(ScheduleGridCellDirective)
  cellTemplates!: QueryList<ScheduleGridCellDirective>;
  templateMap = new Map<string, ScheduleGridCellDirective>();

  // ── Internal state ──────────────────────────────────────

  dayColumns: DayColumn[] = [];
  weekSpans: WeekSpan[] = [];
  monthSpans: MonthSpan[] = [];
  groupedRows: { group: ScheduleGroup | null; rows: ScheduleRow[]; collapsed: boolean }[] = [];
  collapsedGroups = new Set<string | number>();

  /** The computed start of the visible window */
  visibleStart: Date = new Date();
  /** The computed end of the visible window */
  visibleEnd: Date = new Date();

  /** Period label for the header */
  periodLabel = '';

  /** Whether the viewport is mobile-sized (<768px) */
  isMobile = false;

  // Range selection state
  isDragging = false;
  dragRow: ScheduleRow | null = null;
  dragStartColIdx = -1;
  dragEndColIdx = -1;

  /** Short day names for headers (Mon, Tue, …) */
  private shortDayNames: string[] = [];

  /** Single-letter day names for mobile (M, T, O, …) */
  private singleLetterDayNames: string[] = ['S', 'M', 'T', 'O', 'T', 'F', 'L'];

  /** Month names */
  private monthNames: string[] = [];

  /** MediaQueryList for mobile detection */
  private mobileQuery?: MediaQueryList;
  private mobileQueryListener?: () => void;

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.initMobileDetection();
    if (!this.startDate) {
      this.startDate = this.getMondayOfWeek(new Date());
    }
    // On mobile, force week view
    if (this.isMobile && this.viewMode !== 'week') {
      this.viewMode = 'week';
    }
    this.rebuild();
    // Emit initial period so parent can load data on first render
    this.emitPeriodChange();
  }

  ngOnDestroy(): void {
    if (this.mobileQuery && this.mobileQueryListener) {
      this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
    }
  }

  private initMobileDetection(): void {
    if (typeof window === 'undefined') return;
    this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
    this.isMobile = this.mobileQuery.matches;
    this.mobileQueryListener = () => {
      const wasMobile = this.isMobile;
      this.isMobile = this.mobileQuery!.matches;
      if (this.isMobile && !wasMobile) {
        // Entered mobile: force week view
        if (this.viewMode !== 'week' && this.viewMode !== 'two-weeks') {
          this.changeViewMode('week');
        }
        this.rebuild();
      } else if (!this.isMobile && wasMobile) {
        // Left mobile: rebuild to restore full day names
        this.rebuild();
      }
      this.cdr.markForCheck();
    };
    this.mobileQuery.addEventListener('change', this.mobileQueryListener);
  }

  ngAfterContentInit(): void {
    this.cellTemplates.forEach(t => this.templateMap.set(t.name, t));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows'] || changes['groups'] || changes['viewMode'] || changes['startDate']) {
      this.rebuild();
    }
  }

  // ── Navigation ──────────────────────────────────────────

  navigatePrevious(): void {
    if (this.viewMode === 'one-month' || this.viewMode === 'three-months') {
      const months = this.viewMode === 'three-months' ? 3 : 1;
      const d = this.visibleStart;
      this.startDate = new Date(d.getFullYear(), d.getMonth() - months, 1);
    } else {
      this.startDate = this.addDays(this.visibleStart, -this.periodDays());
    }
    this.rebuild();
    this.emitPeriodChange();
  }

  navigateNext(): void {
    if (this.viewMode === 'one-month' || this.viewMode === 'three-months') {
      const months = this.viewMode === 'three-months' ? 3 : 1;
      const d = this.visibleStart;
      this.startDate = new Date(d.getFullYear(), d.getMonth() + months, 1);
    } else {
      this.startDate = this.addDays(this.visibleStart, this.periodDays());
    }
    this.rebuild();
    this.emitPeriodChange();
  }

  navigateToday(): void {
    if (this.viewMode === 'one-month' || this.viewMode === 'three-months') {
      const now = new Date();
      this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      this.startDate = this.getMondayOfWeek(new Date());
    }
    this.rebuild();
    this.emitPeriodChange();
  }

  changeViewMode(mode: ScheduleViewMode): void {
    this.viewMode = mode;
    // Always anchor to today when switching view mode
    const now = new Date();
    if (mode === 'one-month' || mode === 'three-months') {
      this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      this.startDate = this.getMondayOfWeek(now);
    }
    this.rebuild();
    this.viewModeChange.emit(mode);
    this.emitPeriodChange();
  }

  toggleGroup(groupId: string | number): void {
    if (this.collapsedGroups.has(groupId)) {
      this.collapsedGroups.delete(groupId);
    } else {
      this.collapsedGroups.add(groupId);
    }
    this.buildGroupedRows();
    this.cdr.markForCheck();
  }

  // ── Cell interaction ────────────────────────────────────

  onCellClick(row: ScheduleRow, day: DayColumn): void {
    const blocks = row.days[day.isoDate] || [];
    this.cellClick.emit({ row, date: day.date, blocks });
  }

  onBlockClick(event: MouseEvent, row: ScheduleRow, day: DayColumn, block: ScheduleBlock): void {
    event.stopPropagation();
    this.blockClick.emit({ row, date: day.date, block });
  }

  onCellMouseDown(event: MouseEvent, row: ScheduleRow, day: DayColumn): void {
    if (!this.enableRangeSelect) return;
    if (event.button !== 0) return;
    event.preventDefault();
    this.isDragging = true;
    this.dragRow = row;
    const colIdx = this.dayColumns.indexOf(day);
    this.dragStartColIdx = colIdx;
    this.dragEndColIdx = colIdx;
    this.cdr.markForCheck();
  }

  onCellMouseEnter(row: ScheduleRow, day: DayColumn): void {
    if (!this.isDragging || !this.dragRow) return;
    if (row.id !== this.dragRow.id) return;
    this.dragEndColIdx = this.dayColumns.indexOf(day);
    this.cdr.markForCheck();
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (!this.isDragging || !this.dragRow) {
      this.isDragging = false;
      return;
    }

    const startIdx = Math.min(this.dragStartColIdx, this.dragEndColIdx);
    const endIdx = Math.max(this.dragStartColIdx, this.dragEndColIdx);

    if (startIdx === endIdx) {
      this.isDragging = false;
      this.dragRow = null;
      this.dragStartColIdx = -1;
      this.dragEndColIdx = -1;
      this.cdr.markForCheck();
      return;
    }

    const dates: string[] = [];
    for (let i = startIdx; i <= endIdx; i++) {
      dates.push(this.dayColumns[i].isoDate);
    }

    this.rangeSelect.emit({
      row: this.dragRow,
      startDate: this.dayColumns[startIdx].date,
      endDate: this.dayColumns[endIdx].date,
      dates
    });

    this.isDragging = false;
    this.dragRow = null;
    this.dragStartColIdx = -1;
    this.dragEndColIdx = -1;
    this.cdr.markForCheck();
  }

  isCellInDragRange(row: ScheduleRow, dayIdx: number): boolean {
    if (!this.isDragging || !this.dragRow || this.dragRow.id !== row.id) return false;
    const minIdx = Math.min(this.dragStartColIdx, this.dragEndColIdx);
    const maxIdx = Math.max(this.dragStartColIdx, this.dragEndColIdx);
    return dayIdx >= minIdx && dayIdx <= maxIdx;
  }

  // ── Data access helpers (used in template) ──────────────

  getBlocks(row: ScheduleRow, isoDate: string): ScheduleBlock[] {
    return row.days[isoDate] || [];
  }

  getBlockColor(block: ScheduleBlock, index: number): string {
    if (block.color) return block.color;
    return BLOCK_PALETTE[index % BLOCK_PALETTE.length];
  }

  /** Whether the current view mode is compact (dot-only cells) */
  get isCompactMode(): boolean {
    return this.isMobile || this.viewMode === 'one-month' || this.viewMode === 'three-months';
  }

  /** Effective label width — narrower on mobile */
  get effectiveLabelWidth(): number {
    return this.isMobile ? Math.min(this.labelWidth, 120) : this.labelWidth;
  }

  getBlockTooltip(block: ScheduleBlock): string {
    if (block.tooltip) return block.tooltip;
    const parts: string[] = [];
    if (block.label) parts.push(block.label);
    if (block.start && block.end) parts.push(`${block.start} – ${block.end}`);
    return parts.join('\n') || '';
  }

  getInfoValue(row: ScheduleRow, field: string): any {
    return row.meta?.[field] ?? '';
  }

  trackByRowId(_: number, item: { group: ScheduleGroup | null; rows: ScheduleRow[] }): any {
    return item.group?.id ?? 'ungrouped';
  }

  trackByRow(_: number, row: ScheduleRow): any {
    return row.id;
  }

  trackByDay(_: number, day: DayColumn): string {
    return day.isoDate;
  }

  // ── Internal: rebuild everything ────────────────────────

  private rebuild(): void {
    this.buildDayColumns();
    this.buildSpans();
    this.buildGroupedRows();
    this.buildPeriodLabel();
    this.cdr.markForCheck();
  }

  private buildDayColumns(): void {
    const start = this.startDate ? new Date(this.startDate) : this.getMondayOfWeek(new Date());
    // Normalize to midnight
    start.setHours(0, 0, 0, 0);
    this.visibleStart = new Date(start);

    const days = this.periodDays();
    this.visibleEnd = this.addDays(start, days - 1);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIso = this.toIsoDate(today);

    this.dayColumns = [];
    for (let i = 0; i < days; i++) {
      const d = this.addDays(start, i);
      const dow = d.getDay();
      this.dayColumns.push({
        date: d,
        isoDate: this.toIsoDate(d),
        dayOfMonth: d.getDate(),
        dayOfWeek: dow,
        dayName: this.isMobile
          ? (this.singleLetterDayNames[dow] || this.fallbackDayName(dow).charAt(0))
          : (this.shortDayNames[dow] || this.fallbackDayName(dow)),
        isWeekend: dow === 0 || dow === 6,
        isToday: this.toIsoDate(d) === todayIso,
        weekNumber: this.getISOWeek(d),
        monthIndex: d.getMonth(),
        monthLabel: this.monthNames[d.getMonth()] || '',
      });
    }
  }

  private buildSpans(): void {
    // Week spans
    this.weekSpans = [];
    let currentWeek = -1;
    for (const day of this.dayColumns) {
      if (day.weekNumber !== currentWeek) {
        this.weekSpans.push({ weekNumber: day.weekNumber, colSpan: 1, year: day.date.getFullYear() });
        currentWeek = day.weekNumber;
      } else {
        this.weekSpans[this.weekSpans.length - 1].colSpan++;
      }
    }

    // Month spans
    this.monthSpans = [];
    let currentMonth = -1;
    for (const day of this.dayColumns) {
      if (day.monthIndex !== currentMonth) {
        this.monthSpans.push({ label: day.monthLabel, colSpan: 1 });
        currentMonth = day.monthIndex;
      } else {
        this.monthSpans[this.monthSpans.length - 1].colSpan++;
      }
    }
  }

  private buildGroupedRows(): void {
    if (this.groups.length === 0) {
      // No groups — single ungrouped section
      this.groupedRows = [{ group: null, rows: this.rows, collapsed: false }];
      return;
    }

    const rowsByGroup = new Map<string | number, ScheduleRow[]>();
    const ungrouped: ScheduleRow[] = [];

    for (const row of this.rows) {
      if (row.groupId != null) {
        const list = rowsByGroup.get(row.groupId) || [];
        list.push(row);
        rowsByGroup.set(row.groupId, list);
      } else {
        ungrouped.push(row);
      }
    }

    this.groupedRows = this.groups.map(g => ({
      group: g,
      rows: rowsByGroup.get(g.id) || [],
      collapsed: this.collapsedGroups.has(g.id),
    }));

    if (ungrouped.length > 0) {
      this.groupedRows.push({ group: null, rows: ungrouped, collapsed: false });
    }
  }

  private buildPeriodLabel(): void {
    const s = this.visibleStart;
    const e = this.visibleEnd;

    if (this.viewMode === 'week') {
      const wk = this.getISOWeek(s);
      this.periodLabel = `${this.translateService.instant('ScheduleGrid.week')} ${wk}, ${s.getFullYear()}`;
    } else if (this.viewMode === 'two-weeks') {
      const wk1 = this.getISOWeek(s);
      const wk2 = this.getISOWeek(e);
      this.periodLabel = `${this.translateService.instant('ScheduleGrid.week')} ${wk1}–${wk2}, ${s.getFullYear()}`;
    } else if (this.viewMode === 'three-months') {
      const endMonth = new Date(s.getFullYear(), s.getMonth() + 2, 1);
      this.periodLabel = `${this.monthNames[s.getMonth()]} – ${this.monthNames[endMonth.getMonth()]} ${endMonth.getFullYear()}`;
    } else {
      // month / one-month
      this.periodLabel = `${this.monthNames[s.getMonth()]} ${s.getFullYear()}`;
    }
  }

  private emitPeriodChange(): void {
    this.periodChange.emit({
      startDate: new Date(this.visibleStart),
      endDate: new Date(this.visibleEnd),
      viewMode: this.viewMode,
    });
  }

  // ── Date utilities ──────────────────────────────────────

  private periodDays(): number {
    switch (this.viewMode) {
      case 'week': return 7;
      case 'two-weeks': return 14;
      case 'month': return this.daysInMonth(this.visibleStart || new Date());
      case 'one-month': return this.daysInMonth(this.visibleStart || new Date());
      case 'three-months': return this.daysInThreeMonths(this.visibleStart || new Date());
      default: return 7;
    }
  }

  private daysInMonth(d: Date): number {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  }

  private daysInThreeMonths(d: Date): number {
    let total = 0;
    for (let i = 0; i < 3; i++) {
      total += new Date(d.getFullYear(), d.getMonth() + i + 1, 0).getDate();
    }
    return total;
  }

  private getMondayOfWeek(d: Date): Date {
    const result = new Date(d);
    result.setHours(0, 0, 0, 0);
    const day = result.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday = 1
    result.setDate(result.getDate() + diff);
    return result;
  }

  private addDays(d: Date, n: number): Date {
    const result = new Date(d);
    result.setDate(result.getDate() + n);
    return result;
  }

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private getISOWeek(d: Date): number {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  // ── Translations ────────────────────────────────────────

  private loadTranslations(): void {
    // Short day names: index 0=Sunday … 6=Saturday
    this.shortDayNames = [];
    for (let i = 0; i < 7; i++) {
      const key = `DayShortName.${i}`;
      const translated = this.translateService.instant(key);
      this.shortDayNames.push(translated !== key ? translated : this.fallbackDayName(i));
    }

    // Month names: index 0–11
    this.monthNames = [];
    for (let i = 0; i < 12; i++) {
      const key = `Month.${i}`;
      const translated = this.translateService.instant(key);
      this.monthNames.push(translated !== key ? translated : this.fallbackMonthName(i));
    }
  }

  private fallbackDayName(dow: number): string {
    return ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'][dow] || '?';
  }

  private fallbackMonthName(m: number): string {
    return ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'][m] || '?';
  }
}
